import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { api, BACKEND_URL } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import { METRICS, COMMUNITY_PARTNERS, PRIME_EXCLUDE } from "@/lib/postEvent";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } };

const BIG_STATS = [
  { v: METRICS.attendees, l: "Attendees" },
  { v: METRICS.primes, l: "Prime Suppliers" },
  { v: METRICS.formal, l: "Formal Meetings" },
];
const SUB_STATS = [
  { v: METRICS.sbePrime, l: "SBE–Prime Matchmaking Meetings" },
  { v: METRICS.tlPrime, l: "Travel + Leisure–Prime Sourcing Meetings" },
];

export default function Impact() {
  const [primes, setPrimes] = useState([]);
  useEffect(() => {
    api.get("/stations?all=1").then(({ data }) => {
      const seen = new Set();
      const list = [];
      (data.stations || []).forEach((s) => {
        const ex = s.exhibitor || s;
        const name = ex.company_name || s.company_name;
        const logoId = ex.logo_file_id || s.logo_file_id;
        if (!name) return;
        const n = name.toLowerCase().trim();
        if (seen.has(n) || PRIME_EXCLUDE.some((x) => n.includes(x))) return;
        seen.add(n);
        list.push({ name, logo: logoId ? `${BACKEND_URL}/api/files/${logoId}` : null });
      });
      list.sort((a, b) => a.name.localeCompare(b.name));
      setPrimes(list);
    }).catch(() => {});
  }, []);

  return (
    <Layout>
      <section className="bg-[#0A0A0A] text-white" data-testid="impact-hero">
        <div className="container-summit py-20 md:py-28">
          <motion.div {...fade}>
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#C9B792]">Event Impact · Travel + Leisure Strategic Supplier Summit 2026</div>
            <h1 className="serif text-4xl sm:text-6xl mt-6 leading-tight">Measured outcomes.<br />Lasting connections.</h1>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-y-12 gap-x-8">
            {BIG_STATS.map((s) => (
              <div key={s.l} data-testid={`impact-big-${s.l.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
                <div className="serif text-6xl md:text-7xl leading-none">{s.v}</div>
                <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-[#C9B792]">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="mt-14 pt-10 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-y-10 gap-x-8">
            {SUB_STATS.map((s) => (
              <div key={s.l} className="flex items-baseline gap-5">
                <div className="serif text-4xl md:text-5xl text-[#A39171]">{s.v}</div>
                <div className="text-sm text-white/70 max-w-[220px]">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcome narrative */}
      <section className="container-summit py-20 md:py-24" data-testid="impact-narrative">
        <motion.div {...fade} className="max-w-3xl">
          <div className="overline text-[#A39171]">Outcomes</div>
          <h2 className="serif text-3xl md:text-4xl mt-4 leading-tight">A connected supplier ecosystem.</h2>
          <p className="mt-6 text-zinc-600 leading-relaxed">
            The Summit brought Prime Suppliers, Small Business Enterprises, and the Travel + Leisure sourcing team
            into direct conversation — 67 formal meetings across SBE–Prime matchmaking and Travel + Leisure–Prime
            sourcing, alongside an open Supplier Showcase and executive engagement. The result is a stronger,
            better-connected supplier network positioned for second-tier growth.
          </p>
        </motion.div>
      </section>

      {/* Prime recognition */}
      <section className="bg-zinc-50 border-y border-zinc-200" data-testid="impact-primes">
        <div className="container-summit py-20 md:py-24">
          <motion.div {...fade}>
            <div className="overline text-[#A39171]">Participating Prime Suppliers</div>
            <h2 className="serif text-3xl md:text-4xl mt-4">Recognizing our Prime Suppliers</h2>
          </motion.div>
          {primes.length > 0 ? (
            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
              {primes.map((p) => (
                <div key={p.name} className="bg-white px-6 py-8 flex items-center justify-center text-center min-h-[120px]">
                  {p.logo ? <img src={p.logo} alt={p.name} className="max-h-12 max-w-[150px] object-contain" loading="lazy" />
                    : <span className="serif text-lg text-zinc-800 leading-tight">{p.name}</span>}
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-8 text-sm text-zinc-500">Prime Supplier recognition will display here.</p>
          )}
        </div>
      </section>

      {/* Recognition of other groups */}
      <section className="container-summit py-20 md:py-24" data-testid="impact-recognition">
        <motion.div {...fade}>
          <div className="overline text-[#A39171]">With Gratitude</div>
          <h2 className="serif text-3xl md:text-4xl mt-4">Everyone who made the Summit possible</h2>
        </motion.div>
        <div className="mt-12 grid md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
          {[
            { t: "Travel + Leisure Sourcing Team", d: "Executives and sourcing professionals who led engagement and matchmaking." },
            { t: "Small Business Enterprises", d: "Qualified SBEs who connected with Primes and the sourcing team." },
            { t: "Community Partners", d: COMMUNITY_PARTNERS.map((p) => p.name).join(" · ") },
            { t: "Speakers & Summit Guests", d: "Session and panel contributors, and invited partners." },
          ].map((g) => (
            <div key={g.t} className="bg-white p-8">
              <h3 className="serif text-xl">{g.t}</h3>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{g.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0A0A0A] text-white" data-testid="impact-footer-cta">
        <div className="container-summit py-16 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <h3 className="serif text-2xl md:text-3xl">See the day in photographs.</h3>
          <div className="flex gap-3">
            <Link to="/highlights" className="btn-gold" data-testid="impact-cta-highlights">Explore Highlights <ArrowRight size={15} /></Link>
            <Link to="/survey" className="btn-outline-white" data-testid="impact-cta-survey">Complete Survey</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
